import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Smartphone, Monitor, Globe } from 'lucide-react'

const Mockups: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0)

  const mockups = [
    {
      id: 'ios',
      name: 'iOS App',
      icon: Smartphone,
      description: 'Native iOS experience with voice recording and idea management',
      images: [
        {
          title: 'Home Screen',
          description: 'Quick access to all your ideas with status overview',
          mockup: (
            <div className="bg-gray-900 rounded-3xl p-2 w-64 h-[500px] mx-auto">
              <div className="bg-white rounded-2xl h-full p-4 overflow-hidden">
                {/* Status Bar */}
                <div className="flex justify-between items-center mb-4 text-xs">
                  <span>9:41</span>
                  <div className="flex space-x-1">
                    <div className="w-4 h-2 bg-green-500 rounded-sm"></div>
                    <div className="w-6 h-2 bg-gray-300 rounded-sm"></div>
                  </div>
                </div>
                
                {/* Header */}
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-gray-900">Seethink</h1>
                  <p className="text-gray-600 text-sm">See your thoughts, think your ideas</p>
                </div>
                
                {/* Quick Actions */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-red-100 rounded-lg p-3 text-center">
                    <div className="text-2xl mb-1">🎤</div>
                    <div className="text-xs font-medium">Voice</div>
                  </div>
                  <div className="bg-blue-100 rounded-lg p-3 text-center">
                    <div className="text-2xl mb-1">✏️</div>
                    <div className="text-xs font-medium">Text</div>
                  </div>
                  <div className="bg-green-100 rounded-lg p-3 text-center">
                    <div className="text-2xl mb-1">📷</div>
                    <div className="text-xs font-medium">Photo</div>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="text-xs text-gray-600 mb-2">Today's Progress</div>
                  <div className="flex justify-between text-sm">
                    <span>🌱 3 Seeds</span>
                    <span>🌿 2 Sprouts</span>
                    <span>🌳 1 Growing</span>
                  </div>
                </div>
                
                {/* Recent Ideas */}
                <div className="space-y-3">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">🌱 Seed</span>
                      <span className="text-xs text-gray-500">2h ago</span>
                    </div>
                    <h3 className="font-medium text-sm">Product idea for creators</h3>
                    <p className="text-xs text-gray-600 mt-1">Voice recording from subway...</p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs bg-lime-100 text-lime-800 px-2 py-1 rounded">🌿 Sprout</span>
                      <span className="text-xs text-gray-500">1d ago</span>
                    </div>
                    <h3 className="font-medium text-sm">AI writing assistant</h3>
                    <p className="text-xs text-gray-600 mt-1">Expanding on the concept...</p>
                  </div>
                </div>
              </div>
            </div>
          )
        },
        {
          title: 'Voice Recording',
          description: 'Instant voice capture with real-time transcription',
          mockup: (
            <div className="bg-gray-900 rounded-3xl p-2 w-64 h-[500px] mx-auto">
              <div className="bg-white rounded-2xl h-full p-4 flex flex-col justify-center items-center">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-bold mb-2">Recording</h2>
                  <p className="text-gray-600 text-sm">Speak your mind</p>
                </div>
                
                <div className="relative mb-8">
                  <div className="w-32 h-32 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                    <div className="text-white text-4xl">🎤</div>
                  </div>
                  <div className="absolute inset-0 border-4 border-red-300 rounded-full animate-ping"></div>
                </div>
                
                <div className="text-center mb-6">
                  <div className="text-2xl font-mono font-bold text-gray-900">00:23</div>
                  <div className="text-sm text-gray-600">Recording time</div>
                </div>
                
                <div className="flex space-x-4">
                  <button className="bg-gray-200 rounded-full p-3">
                    <div className="w-6 h-6 bg-gray-600 rounded-sm"></div>
                  </button>
                  <button className="bg-red-500 rounded-full p-3">
                    <div className="w-6 h-6 bg-white rounded-full"></div>
                  </button>
                </div>
                
                <div className="mt-6 bg-gray-50 rounded-lg p-3 w-full">
                  <div className="text-xs text-gray-600 mb-1">Live Transcription</div>
                  <div className="text-sm">"I just had an interesting idea about..."</div>
                </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'macos',
      name: 'macOS App',
      icon: Monitor,
      description: 'Powerful desktop editing and publishing tools',
      images: [
        {
          title: 'Main Interface',
          description: 'Dual-pane layout for efficient idea management and editing',
          mockup: (
            <div className="bg-gray-200 rounded-lg p-2 w-full max-w-4xl mx-auto">
              <div className="bg-white rounded-lg h-80 flex">
                {/* Sidebar */}
                <div className="w-1/3 bg-gray-50 p-4 border-r">
                  <div className="mb-4">
                    <h3 className="font-bold text-lg">Seethink</h3>
                    <p className="text-xs text-gray-600">Ideas: 47</p>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="text-xs font-medium text-gray-700">Status</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>🌱 Seeds</span>
                        <span className="text-gray-500">12</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>🌿 Sprouts</span>
                        <span className="text-gray-500">8</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>🌳 Growing</span>
                        <span className="text-gray-500">5</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>🍎 Mature</span>
                        <span className="text-gray-500">3</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-700">Recent</div>
                    <div className="space-y-2">
                      <div className="bg-white rounded p-2 border">
                        <div className="text-sm font-medium">Mobile UX patterns</div>
                        <div className="text-xs text-gray-600">2 hours ago</div>
                      </div>
                      <div className="bg-white rounded p-2 border">
                        <div className="text-sm font-medium">AI writing tools</div>
                        <div className="text-xs text-gray-600">1 day ago</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Main Content */}
                <div className="flex-1 p-4">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-lg font-bold">Mobile UX Design Patterns</h2>
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded">🌳 Growing</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-4">Created 2 hours ago • Last edited now</div>
                  </div>
                  
                  <div className="bg-gray-50 rounded p-3 mb-4 text-sm font-mono">
                    <div className="flex space-x-4 mb-2">
                      <button className="text-gray-600 hover:text-gray-900">B</button>
                      <button className="text-gray-600 hover:text-gray-900">I</button>
                      <button className="text-gray-600 hover:text-gray-900">#</button>
                      <button className="text-gray-600 hover:text-gray-900">[]</button>
                    </div>
                  </div>
                  
                  <div className="text-sm space-y-2">
                    <div className="font-mono"># Mobile UX Design Patterns</div>
                    <div className="font-mono text-gray-600">## Key Observations</div>
                    <div className="font-mono text-gray-700">
                      Users expect familiar patterns when navigating mobile interfaces...
                    </div>
                    <div className="font-mono text-gray-700">
                      - Bottom navigation for primary actions
                    </div>
                    <div className="font-mono text-gray-700">
                      - Swipe gestures for secondary functions
                    </div>
                  </div>
                  
                  <div className="mt-4 flex space-x-2">
                    <button className="bg-primary-600 text-white px-3 py-1 rounded text-sm">Save</button>
                    <button className="bg-green-600 text-white px-3 py-1 rounded text-sm">Publish</button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: 'web',
      name: 'Web App',
      icon: Globe,
      description: 'Cross-platform access with PWA capabilities',
      images: [
        {
          title: 'Dashboard',
          description: 'Responsive web interface for idea management',
          mockup: (
            <div className="bg-white rounded-lg border w-full max-w-4xl mx-auto h-80 overflow-hidden">
              {/* Browser Bar */}
              <div className="bg-gray-100 px-4 py-2 border-b flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="bg-white rounded px-3 py-1 text-sm text-gray-600 flex-1">
                  seethink.app
                </div>
              </div>
              
              {/* Web Content */}
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h1 className="text-2xl font-bold">Seethink</h1>
                    <p className="text-gray-600">Your ideas, everywhere</p>
                  </div>
                  <button className="bg-primary-600 text-white px-4 py-2 rounded">+ New Idea</button>
                </div>
                
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🌱</div>
                    <div className="font-bold text-lg">12</div>
                    <div className="text-sm text-gray-600">Seeds</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🌿</div>
                    <div className="font-bold text-lg">8</div>
                    <div className="text-sm text-gray-600">Sprouts</div>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🌳</div>
                    <div className="font-bold text-lg">5</div>
                    <div className="text-sm text-gray-600">Growing</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🍎</div>
                    <div className="font-bold text-lg">3</div>
                    <div className="text-sm text-gray-600">Mature</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">🌱 Seed</span>
                      <span className="text-xs text-gray-500">2h ago</span>
                    </div>
                    <h3 className="font-medium mb-1">Product Strategy Ideas</h3>
                    <p className="text-sm text-gray-600">Thoughts on user acquisition...</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="bg-lime-100 text-lime-800 text-xs px-2 py-1 rounded">🌿 Sprout</span>
                      <span className="text-xs text-gray-500">1d ago</span>
                    </div>
                    <h3 className="font-medium mb-1">Design System Concepts</h3>
                    <p className="text-sm text-gray-600">Building scalable components...</p>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]
    }
  ]

  return (
    <section id="mockups" className="section-padding bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            See It In <span className="gradient-text">Action</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience Seethink across all platforms with our interactive previews
          </p>
        </motion.div>

        {/* Platform Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="flex justify-center mb-12"
        >
          <div className="bg-gray-100 rounded-lg p-1 flex space-x-1">
            {mockups.map((platform, index) => (
              <button
                key={platform.id}
                onClick={() => setActiveTab(index)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  activeTab === index
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <platform.icon size={18} />
                <span className="font-medium">{platform.name}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Platform Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-8"
          >
            <h3 className="text-2xl font-bold mb-2">{mockups[activeTab].name}</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {mockups[activeTab].description}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Mockup Display */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeTab}-mockup`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="bg-gray-50 rounded-2xl p-8"
          >
            {mockups[activeTab].images.map((image, index) => (
              <div key={index} className="mb-12 last:mb-0">
                <div className="text-center mb-6">
                  <h4 className="text-xl font-semibold mb-2">{image.title}</h4>
                  <p className="text-gray-600">{image.description}</p>
                </div>
                <div className="flex justify-center">
                  {image.mockup}
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Feature Callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-16 bg-gradient-to-r from-primary-50 to-green-50 rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold mb-4">
            <span className="gradient-text">Seamless Experience</span> Across All Devices
          </h3>
          <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
            Start capturing ideas on your Apple Watch, develop them on your iPhone, 
            write detailed content on your Mac, and access everything from the web. 
            Your creative workflow, uninterrupted.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://github.com/oiahoon/seethink"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Try It Now
            </motion.a>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-secondary"
            >
              Watch Demo
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Mockups
